import { useCallback, useEffect, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  ApiKeysError,
  listApiKeys,
  revokeApiKey,
  type ApiKeyListItem,
  type ApiKeysErrorReason,
} from '@/lib/api_keys';
import { ApiKeyAddDialog } from '@/components/ApiKeys/ApiKeyAddDialog';
import { ApiKeyRotateDialog } from '@/components/ApiKeys/ApiKeyRotateDialog';
import { ApiKeysListView } from '@/components/ApiKeys/ApiKeysListView';
import { mapApiKeysErrorReason } from '@/components/ApiKeys/error_messages';
import { Button } from '@/components/ui';

export function ApiKeysPage() {
  const apiClient = useApiClient();
  const [items, setItems] = useState<ApiKeyListItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [rotateTarget, setRotateTarget] = useState<ApiKeyListItem | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyListItem | null>(null);
  const [revokeBusyId, setRevokeBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await listApiKeys(apiClient);
      setItems(list);
    } catch (err) {
      const reason: ApiKeysErrorReason = err instanceof ApiKeysError ? err.reason : 'unknown';
      setLoadError(mapApiKeysErrorReason(reason));
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function handleAddSuccess(item: ApiKeyListItem) {
    setItems((prev) => (prev ? [...prev, item] : [item]));
    setAddOpen(false);
    setActionError(null);
    setActionSuccess(ptBR.apiKeys.saveSuccess);
  }

  function handleRotateSuccess(item: ApiKeyListItem) {
    setItems((prev) => (prev ? prev.map((it) => (it.id === item.id ? item : it)) : [item]));
    setRotateTarget(null);
    setActionError(null);
    setActionSuccess(ptBR.apiKeys.rotateSuccess);
  }

  async function handleRevokeConfirm() {
    if (revokeTarget === null) return;
    const target = revokeTarget;
    setRevokeBusyId(target.id);
    setActionError(null);
    try {
      await revokeApiKey(apiClient, target.id);
      setItems((prev) => (prev ? prev.filter((it) => it.id !== target.id) : prev));
      setActionSuccess(ptBR.apiKeys.revokeSuccess);
      setRevokeTarget(null);
    } catch (err) {
      const reason: ApiKeysErrorReason = err instanceof ApiKeysError ? err.reason : 'unknown';
      setActionError(mapApiKeysErrorReason(reason));
    } finally {
      setRevokeBusyId(null);
    }
  }

  return (
    <section
      aria-labelledby="api-keys-title"
      data-testid="api-keys-page"
      className="mx-auto max-w-3xl"
    >
      <p className="mb-1.5 font-display text-[11px] uppercase tracking-[0.16em] text-fg-dim">
        {ptBR.apiKeys.eyebrow}
      </p>
      <h1 id="api-keys-title" className="text-display-md text-fg">
        {ptBR.apiKeys.pageTitle}
      </h1>
      <p className="mt-2 text-body-md text-fg-muted">{ptBR.apiKeys.intro}</p>

      <div className="mt-6 flex justify-end">
        <Button type="button" onClick={() => setAddOpen(true)} data-testid="api-keys-add-cta">
          {ptBR.apiKeys.addCta}
        </Button>
      </div>

      {actionSuccess !== null ? (
        <p
          role="status"
          data-testid="api-keys-action-success"
          className="mt-4 border border-success bg-success-50 px-3 py-2 text-body-sm text-success-700"
        >
          {actionSuccess}
        </p>
      ) : null}
      {actionError !== null ? (
        <p
          role="alert"
          data-testid="api-keys-action-error"
          className="mt-4 border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
        >
          {actionError}
        </p>
      ) : null}

      <ApiKeysListView
        loading={loading}
        loadError={loadError}
        items={items}
        revokeTarget={revokeTarget}
        revokeBusyId={revokeBusyId}
        onRetry={() => void refresh()}
        onRotateClick={(item) => setRotateTarget(item)}
        onRevokeClick={(item) => setRevokeTarget(item)}
        onRevokeConfirm={() => void handleRevokeConfirm()}
        onRevokeCancel={() => setRevokeTarget(null)}
      />

      {addOpen ? (
        <ApiKeyAddDialog onSuccess={handleAddSuccess} onCancel={() => setAddOpen(false)} />
      ) : null}

      {rotateTarget !== null ? (
        <ApiKeyRotateDialog
          item={rotateTarget}
          onSuccess={handleRotateSuccess}
          onCancel={() => setRotateTarget(null)}
        />
      ) : null}
    </section>
  );
}
